import { z } from 'zod'
import { normalizeCountryIso3 } from './countries'

/**
 * Citizenship region presets (PLAN v4 §1.1). Requirements store region IDS (`citizenship.regions`) plus explicit
 * countries; the union is expanded at evaluation time (`expandCitizenship`), so an EU enlargement is a code change,
 * never a data migration. Lists: planning/research-geo-areas.md §6 (UN M49 parsed 2026-10-08; political groupings
 * from their official pages). M49 has no TWN / XKK: added by hand (Asia / Europe).
 */

export const REGION_IDS = [
  'EU27',
  'EEA',
  'SCHENGEN',
  'COUNCIL_OF_EUROPE',
  'EUROPE',
  'LATAM',
  'LATAM_CARIBBEAN',
  'NORTH_AMERICA',
  'SOUTH_AMERICA',
  'AFRICA',
  'ASIA',
  'OCEANIA',
  'MIDDLE_EAST',
] as const
export const RegionIdSchema = z.enum(REGION_IDS)
export type RegionId = z.infer<typeof RegionIdSchema>

export interface RegionPreset {
  id: RegionId
  label: string
  /** Where the list comes from (shown as a hint in the organizer UI). */
  source: string
  /** Date the list was checked. */
  asOf: string
  iso3: readonly string[]
}

const codes = (s: string): readonly string[] => s.trim().split(/\s+/)

const EU27 = codes(
  'AUT BEL BGR HRV CYP CZE DNK EST FIN FRA DEU GRC HUN IRL ITA LVA LTU LUX MLT NLD POL PRT ROU SVK SVN ESP SWE'
)

export const REGIONS: Readonly<Record<RegionId, RegionPreset>> = {
  EU27: {
    id: 'EU27',
    label: 'European Union (27)',
    source: 'https://european-union.europa.eu/principles-countries-history/eu-countries_en',
    asOf: '2026-10-08',
    iso3: EU27,
  },
  EEA: {
    id: 'EEA',
    label: 'European Economic Area (EU + Iceland, Liechtenstein, Norway)',
    source: 'EU27 + ISL LIE NOR',
    asOf: '2026-10-08',
    iso3: [...EU27, 'ISL', 'LIE', 'NOR'],
  },
  SCHENGEN: {
    id: 'SCHENGEN',
    label: 'Schengen Area (29)',
    source: 'https://home-affairs.ec.europa.eu/policies/schengen/schengen-area_en',
    asOf: '2026-10-08',
    iso3: [...EU27.filter((c) => c !== 'CYP' && c !== 'IRL'), 'ISL', 'LIE', 'NOR', 'CHE'],
  },
  COUNCIL_OF_EUROPE: {
    id: 'COUNCIL_OF_EUROPE',
    label: 'Council of Europe (46)',
    source: 'https://www.coe.int/en/web/portal/46-members-states',
    asOf: '2026-10-08',
    iso3: codes(
      'ALB AND ARM AUT AZE BEL BIH BGR HRV CYP CZE DNK EST FIN FRA GEO DEU GRC HUN ISL IRL ITA LVA LIE LTU LUX MLT MDA MCO MNE NLD MKD NOR POL PRT ROU SMR SRB SVK SVN ESP SWE CHE TUR UKR GBR'
    ),
  },
  EUROPE: {
    id: 'EUROPE',
    label: 'Europe (geographic, UN M49)',
    source: 'UN M49 region 150 + XKK',
    asOf: '2026-10-08',
    iso3: codes(
      'ALA ALB AND AUT BEL BGR BIH BLR CHE CZE DEU DNK ESP EST FIN FRA FRO GBR GGY GIB GRC HRV HUN IMN IRL ISL ITA JEY LIE LTU LUX LVA MCO MDA MKD MLT MNE NLD NOR POL PRT ROU RUS SJM SMR SRB SVK SVN SWE UKR VAT XKK'
    ),
  },
  LATAM: {
    id: 'LATAM',
    label: 'Latin America (19 Spanish- and Portuguese-speaking countries)',
    source: 'Convention (all in UN M49 419)',
    asOf: '2026-10-08',
    iso3: codes('ARG BOL BRA CHL COL CRI CUB DOM ECU SLV GTM HND MEX NIC PAN PRY PER URY VEN'),
  },
  LATAM_CARIBBEAN: {
    id: 'LATAM_CARIBBEAN',
    label: 'Latin America and the Caribbean (UN M49)',
    source: 'UN M49 region 419',
    asOf: '2026-10-08',
    iso3: codes(
      'ABW AIA ARG ATG BES BHS BLM BLZ BOL BRA BRB BVT CHL COL CRI CUB CUW CYM DMA DOM ECU FLK GLP GRD GTM GUF GUY HND HTI JAM KNA LCA MAF MEX MSR MTQ NIC PAN PER PRI PRY SGS SLV SUR SXM TCA TTO URY VCT VEN VGB VIR'
    ),
  },
  NORTH_AMERICA: {
    id: 'NORTH_AMERICA',
    label: 'North America (Canada, Mexico, United States)',
    source: 'USMCA convention (UN M49 021 Northern America is BMU CAN GRL SPM USA)',
    asOf: '2026-10-08',
    iso3: codes('CAN MEX USA'),
  },
  SOUTH_AMERICA: {
    id: 'SOUTH_AMERICA',
    label: 'South America (UN M49)',
    source: 'UN M49 region 005',
    asOf: '2026-10-08',
    iso3: codes('ARG BOL BRA BVT CHL COL ECU FLK GUF GUY PER PRY SGS SUR URY VEN'),
  },
  AFRICA: {
    id: 'AFRICA',
    label: 'Africa (UN M49)',
    source: 'UN M49 region 002',
    asOf: '2026-10-08',
    iso3: codes(
      'AGO ATF BDI BEN BFA BWA CAF CIV CMR COD COG COM CPV DJI DZA EGY ERI ESH ETH GAB GHA GIN GMB GNB GNQ IOT KEN LBR LBY LSO MAR MDG MLI MOZ MRT MUS MWI MYT NAM NER NGA REU RWA SDN SEN SHN SLE SOM SSD STP SWZ SYC TCD TGO TUN TZA UGA ZAF ZMB ZWE'
    ),
  },
  ASIA: {
    id: 'ASIA',
    label: 'Asia (UN M49)',
    source: 'UN M49 region 142 + TWN',
    asOf: '2026-10-08',
    iso3: codes(
      'AFG ARE ARM AZE BGD BHR BRN BTN CHN CYP GEO HKG IDN IND IRN IRQ ISR JOR JPN KAZ KGZ KHM KOR KWT LAO LBN LKA MAC MDV MMR MNG MYS NPL OMN PAK PHL PRK PSE QAT SAU SGP SYR THA TJK TKM TLS TUR UZB VNM YEM TWN'
    ),
  },
  OCEANIA: {
    id: 'OCEANIA',
    label: 'Oceania (UN M49)',
    source: 'UN M49 region 009',
    asOf: '2026-10-08',
    iso3: codes(
      'ASM AUS CCK COK CXR FJI FSM GUM HMD KIR MHL MNP NCL NFK NIU NRU NZL PCN PLW PNG PYF SLB TKL TON TUV UMI VUT WLF WSM'
    ),
  },
  MIDDLE_EAST: {
    id: 'MIDDLE_EAST',
    label: 'Middle East',
    source: 'Convention (no UN M49 group)',
    asOf: '2026-10-08',
    iso3: codes('BHR CYP EGY IRN IRQ ISR JOR KWT LBN OMN PSE QAT SAU SYR TUR ARE YEM'),
  },
}

/**
 * Accepted citizenships: union of the regions' countries and the explicit countries, upper-case ISO3, unknown
 * codes dropped, sorted, without duplicates. Used by the policy, eligibility, the Didit workflow
 * (`documents_allowed`) and the organizer UI ("N countries").
 */
export function expandCitizenship(regions: readonly RegionId[], countries: readonly string[]): string[] {
  const out = new Set<string>()
  for (const id of regions) for (const c of REGIONS[id]?.iso3 ?? []) out.add(c)
  for (const c of countries) {
    const iso3 = normalizeCountryIso3(c)
    if (iso3) out.add(iso3)
  }
  return [...out].sort()
}
